import { type PropsWithChildren } from 'react'
import { useAppearanceTransition } from '../animation/hooks/useAppearanceTransition'
import { AnimatePresence, motion } from 'motion/react'

export interface AppearanceTransitionProps {
  isVisible: boolean
  alignment: 'top' | 'bottom'
}

/**
 * @name AppearanceTransition
 * @description A component that animates the presence of its children based on a boolean state.
 * It uses `AnimatePresence` from `motion/react` to handle enter and exit animations.
 *

 * @param {boolean} props.isVisible - A boolean that determines if the children are visible.
 * @param {'top' | 'bottom'} props.alignment - A value that determines which direction if the children should animate in from
 * @param {React.ReactNode} props.children - The content to be conditionally rendered and animated.
 *
 * @example
 * ```tsx
 * import { Transition } from './transition'
 *
 * const MyComponent = () => {
 *   const [isVisible, setIsVisible] = useState(true)
 *
 *   return (
 *     <div>
 *       <button onClick={() => setIsVisible(!isVisible)}>
 *         Toggle Visibility
 *       </button>
 *       <AppearanceTransition isVisible={isVisible}>
 *         <div>Hello, I am animated!</div>
 *       </AppearanceTransition>
 *     </div>
 *   )
 * }
 * ```
 */
export const AppearanceTransition = (props: PropsWithChildren<AppearanceTransitionProps>) => {
  const { isVisible, alignment, children } = props
  const animationProps = useAppearanceTransition(alignment)
  return (
    <AnimatePresence>
      {isVisible && <motion.div {...animationProps}>{children}</motion.div>}
    </AnimatePresence>
  )
}
